import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { isAdmin, logSecurityEvent } from "@/lib/dashboardAuth";

export const Route = createFileRoute("/auth/otp")({
  validateSearch: (s: Record<string, unknown>) => ({
    email: typeof s.email === "string" ? s.email : "",
  }),
  head: () => ({
    meta: [
      { title: "Verify Sign-In | Homestead Construction" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: OtpPage,
});

function OtpPage() {
  const navigate = useNavigate();
  const { email } = useSearch({ from: "/auth/otp" });
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  if (!email) {
    if (typeof window !== "undefined") navigate({ to: "/auth", replace: true });
    return null;
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token: code,
      type: "email",
    });
    if (error || !data.user) {
      setLoading(false);
      await logSecurityEvent("otp_failed", false, email);
      toast.error("Invalid or expired code.");
      return;
    }
    const admin = await isAdmin(data.user.id);
    if (!admin) {
      await supabase.auth.signOut();
      setLoading(false);
      await logSecurityEvent("otp_unauthorized", false, email);
      toast.error("This account isn't authorized.");
      return;
    }
    await logSecurityEvent("otp_success", true, email);
    setLoading(false);
    navigate({ to: "/dashboard", replace: true });
  };

  const resend = async () => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false },
    });
    if (error) toast.error("Couldn't resend the code.");
    else toast.success("New code sent.");
  };

  return (
    <div className="min-h-screen bg-surface-alt flex items-center justify-center p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-card border border-border rounded-2xl p-8 space-y-5 shadow-xl"
      >
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">Check your email</h1>
          <p className="text-muted-foreground text-sm">
            We sent a 6-digit code to {email}. Enter it below.
          </p>
        </div>
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          required
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          className="w-full text-center text-2xl tracking-[0.5em] px-4 py-4 rounded-lg bg-background border border-border focus:border-brand focus:outline-none font-mono"
          placeholder="000000"
        />
        <button
          type="submit"
          disabled={loading || code.length !== 6}
          className="w-full py-3 rounded-lg bg-brand hover:bg-brand-hover text-white font-display font-bold disabled:opacity-50"
        >
          {loading ? "Verifying…" : "Verify"}
        </button>
        <button
          type="button"
          onClick={resend}
          className="w-full text-sm text-brand hover:underline"
        >
          Resend code
        </button>
      </form>
    </div>
  );
}
